/**
 * LIB lane — the structured diff between a library block and a recipe process.
 *
 * Self-contained and layout-agnostic: it renders inside a Drawer, a Modal, a Collapse
 * panel or straight into a page. Feed it the `diff` object from
 * /library/lineage/process/ or a `usages[].diff` row from /library/impact/.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Empty, Space, Tag, Tooltip, Typography } from 'antd';

const { Text } = Typography;

const CHANGE_META = {
  ADDED_IN_RECIPE: { color: 'orange', label: 'Added in recipe' },
  MISSING_IN_RECIPE: { color: 'red', label: 'Missing from recipe' },
  MODIFIED: { color: 'gold', label: 'Modified' },
  REORDERED: { color: 'blue', label: 'Reordered' },
};

const COLLECTION_LABEL = {
  parameters: 'Parameters',
  ingredients: 'Ingredients',
  instructions: 'Instructions',
  sampling: 'Sampling',
};

const show = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const FieldRow = ({ field }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', lineHeight: 1.7 }}>
    <Text type="secondary" style={{ minWidth: 170 }}>
      {field.label || field.field}
    </Text>
    <Text delete type="secondary" style={{ maxWidth: 260, wordBreak: 'break-word' }}>
      {show(field.library_value)}
    </Text>
    <Text type="secondary">→</Text>
    <Text strong style={{ maxWidth: 260, wordBreak: 'break-word' }}>
      {show(field.recipe_value)}
    </Text>
  </div>
);

FieldRow.propTypes = { field: PropTypes.object.isRequired };

const CollectionRow = ({ name, delta }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', lineHeight: 1.7 }}>
    <Text type="secondary" style={{ minWidth: 170 }}>
      {COLLECTION_LABEL[name] || name}
    </Text>
    <Text type="secondary">
      library {delta.library_count} → recipe {delta.recipe_count}
    </Text>
    {delta.added_in_recipe?.length > 0 && (
      <Tag color="orange">+{delta.added_in_recipe.length} in recipe</Tag>
    )}
    {delta.missing_in_recipe?.length > 0 && (
      <Tag color="red">−{delta.missing_in_recipe.length} vs library</Tag>
    )}
  </div>
);

CollectionRow.propTypes = { name: PropTypes.string.isRequired, delta: PropTypes.object.isRequired };

const ProcessDiffView = ({ diff, notCompared }) => {
  if (!diff) return <Empty description="No comparison available" />;

  const rows = diff.operations || [];

  return (
    <Space direction="vertical" size={12} style={{ width: '100%' }}>
      <Space size={6} wrap>
        <Tag>{diff.library_operation_count} operation(s) in library block</Tag>
        <Tag>{diff.recipe_operation_count} operation(s) in recipe</Tag>
        <Tag color={diff.changed_operation_count ? 'orange' : 'green'}>
          {diff.changed_operation_count} differing
        </Tag>
      </Space>

      {rows.length === 0 ? (
        <Alert type="success" showIcon message="This process matches its library block on everything compared." />
      ) : (
        rows.map((op, i) => {
          const meta = CHANGE_META[op.change] || { color: 'default', label: op.change };
          return (
            <div
              key={`${op.operation_name}-${i}`}
              style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}
            >
              <Space size={8} wrap style={{ marginBottom: op.fields?.length || op.collections ? 6 : 0 }}>
                <Tag color={meta.color}>{meta.label}</Tag>
                <Text strong>{op.operation_name || '(unnamed operation)'}</Text>
                {op.library_position && op.recipe_position && op.library_position !== op.recipe_position && (
                  <Tooltip title="Position within the process">
                    <Tag color="blue">
                      step {op.library_position} → {op.recipe_position}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
              {(op.fields || []).map((f) => (
                <FieldRow key={f.field} field={f} />
              ))}
              {Object.entries(op.collections || {}).map(([name, delta]) => (
                <CollectionRow key={name} name={name} delta={delta} />
              ))}
            </div>
          );
        })
      )}

      {notCompared?.length > 0 && (
        <Alert
          type="info"
          showIcon
          message="Not covered by this comparison"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {notCompared.map((n) => (
                <li key={n}>{n}</li>
              ))}
            </ul>
          }
        />
      )}
    </Space>
  );
};

ProcessDiffView.propTypes = {
  diff: PropTypes.object,
  notCompared: PropTypes.arrayOf(PropTypes.string),
};

export default ProcessDiffView;
